import { useEffect, useState } from 'react';
import Modal from './Modal';
import { Clock, Loader2, Save, Trash2, X } from 'lucide-react';
import { useApi } from '../api/client';
import { useCatalogs } from '../api/useCatalogs';
import type { Contact } from '../types/api';

// Shared contact editor. Extracted from Contacts.tsx so the Legacy Spark
// Chats and Discovery Calls grids can open a contact in place (by clicking a
// name) and return to the grid on close.

export interface ContactFormState {
  id: number | null;
  name: string;
  platform_id: number | '';
  email: string;
  linkedin_url: string;
  phone: string;
  company_or_org: string;
  notes: string;
}

export const EMPTY_CONTACT_FORM: ContactFormState = {
  id: null,
  name: '',
  platform_id: '',
  email: '',
  linkedin_url: '',
  phone: '',
  company_or_org: '',
  notes: '',
};

export function fromContact(c: Contact): ContactFormState {
  return {
    id: c.id,
    name: c.name,
    platform_id: c.platform_id ?? '',
    email: c.email ?? '',
    linkedin_url: c.linkedin_url ?? '',
    phone: c.phone ?? '',
    company_or_org: c.company_or_org ?? '',
    notes: c.notes ?? '',
  };
}

interface ActivityTouch {
  type: 'touch';
  outreach_id: number;
  occurred_at: string | null;
  touch_kind: string | null;
  touch_kind_label: string | null;
  contact_method: string | null;
  status: string | null;
  status_label: string | null;
}

interface ActivityStatus {
  type: 'status';
  status_event_id: number;
  occurred_at: string | null;
  status: string | null;
  status_label: string | null;
}

type ActivityEntry = ActivityTouch | ActivityStatus;

interface ContactEditModalProps {
  form: ContactFormState | null;
  onClose: () => void;
  onSaved: () => void;
  onChanged?: () => void;
}

export default function ContactEditModal({ form, onClose, onSaved, onChanged }: ContactEditModalProps) {
  const apiFetch = useApi();
  const { catalogs } = useCatalogs();
  const [draft, setDraft] = useState<ContactFormState>(EMPTY_CONTACT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[] | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [activityTick, setActivityTick] = useState(0);

  useEffect(() => {
    if (form) {
      setDraft(form);
      setError(null);
    }
  }, [form]);

  // Fetch the merged activity feed (touches + manual status changes) for
  // existing contacts. New contacts have nothing to show. activityTick lets a
  // successful removal force a refetch.
  useEffect(() => {
    if (!form || form.id == null) {
      setActivity(null);
      setActivityError(null);
      return;
    }
    const contactId = form.id;
    let cancelled = false;
    setActivity(null);
    setActivityError(null);
    apiFetch(`/contacts/${contactId}/activity`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: { activity: ActivityEntry[] }) => {
        if (!cancelled) setActivity(d.activity);
      })
      .catch((e) => {
        if (!cancelled) setActivityError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [form, apiFetch, activityTick]);

  if (!form) return null;
  const isNew = form.id == null;

  function setField<K extends keyof ContactFormState>(
    key: K,
    value: ContactFormState[K],
  ) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (!draft.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: draft.name.trim(),
        platform_id: draft.platform_id === '' ? null : draft.platform_id,
        email: draft.email.trim() || null,
        linkedin_url: draft.linkedin_url.trim() || null,
        phone: draft.phone.trim() || null,
        company_or_org: draft.company_or_org.trim() || null,
        notes: draft.notes.trim() || null,
      };
      const r = await apiFetch(
        isNew ? '/contacts' : `/contacts/${draft.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          body: JSON.stringify(body),
        },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onSaved();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  async function softDelete() {
    if (isNew || !draft.id) return;
    if (!confirm(`Delete contact "${draft.name}"? This is reversible.`)) return;
    setSaving(true);
    try {
      const r = await apiFetch(`/contacts/${draft.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onSaved();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeEntry(entry: ActivityEntry) {
    if (!draft.id) return;
    const key =
      entry.type === 'touch'
        ? `touch-${entry.outreach_id}`
        : `status-${entry.status_event_id}`;
    const label =
      entry.type === 'touch'
        ? entry.touch_kind_label ?? 'this touch'
        : entry.status_label ?? 'this status change';
    if (!confirm(`Remove ${label}? This can't be undone.`)) return;
    const path =
      entry.type === 'touch'
        ? `/contacts/${draft.id}/touches/${entry.outreach_id}`
        : `/contacts/${draft.id}/status-events/${entry.status_event_id}`;
    setRemovingKey(key);
    setError(null);
    try {
      const r = await apiFetch(path, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setActivityTick((t) => t + 1);
      onChanged?.();
    } catch (err) {
      setError(String(err));
    } finally {
      setRemovingKey(null);
    }
  }

  return (
    <Modal onClose={onClose}>
          <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-brand-mint to-brand-sky px-5 py-3 text-brand-forest">
            <div className="font-display font-black">
              {isNew ? 'New contact' : draft.name || 'Edit contact'}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 transition hover:bg-brand-forest/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={save} className="space-y-4 px-5 py-5">
            <Field label="Name">
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setField('name', e.target.value)}
                required
                className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
              />
            </Field>

            {catalogs && (
              <Field label="Platform (where she lives)">
                <select
                  value={draft.platform_id}
                  onChange={(e) =>
                    setField(
                      'platform_id',
                      e.target.value === '' ? '' : Number(e.target.value),
                    )
                  }
                  className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                >
                  <option value="">—</option>
                  {catalogs.platforms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.description}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <input
                  type="email"
                  value={draft.email}
                  onChange={(e) => setField('email', e.target.value)}
                  className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  value={draft.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                />
              </Field>
            </div>

            <Field label="LinkedIn URL">
              <input
                type="url"
                value={draft.linkedin_url}
                onChange={(e) => setField('linkedin_url', e.target.value)}
                className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
              />
            </Field>

            <Field label="Company / org">
              <input
                type="text"
                value={draft.company_or_org}
                onChange={(e) => setField('company_or_org', e.target.value)}
                className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
              />
            </Field>

            <Field label="Notes">
              <textarea
                value={draft.notes}
                onChange={(e) => setField('notes', e.target.value)}
                rows={3}
                className="w-full resize-none rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
              />
            </Field>

            {!isNew && (
              <div>
                <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-sage">
                  <Clock className="h-3.5 w-3.5" />
                  Activity
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border border-brand-sage/40 bg-brand-paper">
                  {activityError ? (
                    <div className="px-3 py-2 text-sm text-red-700">
                      {activityError}
                    </div>
                  ) : activity == null ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-brand-sage">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading…
                    </div>
                  ) : activity.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-brand-sage">
                      No activity yet.
                    </div>
                  ) : (
                    <ul className="divide-y divide-brand-sage/20">
                      {activity.map((entry) => {
                        const key =
                          entry.type === 'touch'
                            ? `touch-${entry.outreach_id}`
                            : `status-${entry.status_event_id}`;
                        return (
                          <li
                            key={key}
                            className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm"
                          >
                            <span className="w-14 shrink-0 text-brand-sage">
                              {entry.occurred_at
                                ? new Date(entry.occurred_at).toLocaleDateString()
                                : '—'}
                            </span>
                            <span className="flex-1 text-brand-forest">
                              {entry.type === 'touch' ? (
                                <>
                                  <span className="font-bold">
                                    {entry.touch_kind_label ?? 'Touch'}
                                  </span>
                                  {entry.contact_method ? (
                                    <span className="text-brand-sage">
                                      {' · '}
                                      {entry.contact_method}
                                    </span>
                                  ) : null}
                                  {entry.status_label ? (
                                    <span className="text-brand-sage">
                                      {' → '}
                                      {entry.status_label}
                                    </span>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <span className="font-bold">
                                    {entry.status_label ?? entry.status ?? '—'}
                                  </span>
                                  <span className="text-brand-sage"> (manual)</span>
                                </>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeEntry(entry)}
                              disabled={removingKey === key}
                              title="Remove"
                              className="shrink-0 rounded p-1 text-brand-sage transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            >
                              {removingKey === key ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between border-t border-brand-sage/20 pt-3">
              {isNew ? (
                <span />
              ) : (
                <button
                  type="button"
                  onClick={softDelete}
                  disabled={saving}
                  className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md px-4 py-2 text-sm text-brand-forest hover:bg-brand-sage/10"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-sage">
        {label}
      </label>
      {children}
    </div>
  );
}